import { Box, CircularProgress, Typography } from "@mui/material";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { queryKeys } from "../../../queries/keys";
import { getTimeEfficiencyMonthlyEvolution } from "../../time-efficiency-live/api/timeEfficiency.api";
import {
  TE_CARD_SX,
  TE_COLORS,
  TE_PANEL_DESC_SX,
  TE_PANEL_TITLE_SX,
} from "./theme";

const MESES = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

/** #MMV-2615: dados reais (Api) -- média de dias por mês de início do
 * projeto, mês atual + 5 anteriores, sem filtro de célula/status (visão
 * geral da empresa, igual ao mockup de referência). */
export function LineChart() {
  const { data: entries, isLoading } = useQuery(
    queryKeys.timeEfficiencyLiveMonthlyEvolution,
    getTimeEfficiencyMonthlyEvolution
  );

  const { meses, valores, counts } = useMemo(() => {
    const sorted = [...(entries ?? [])].sort((a, b) =>
      a.month.localeCompare(b.month)
    );
    const meses = sorted.map((e) => {
      const [yy, mm] = e.month.split("-");
      return `${MESES[parseInt(mm, 10) - 1]}/${yy.slice(-2)}`;
    });
    const valores = sorted.map(
      (e) => Math.round((e.averageDurationSeconds / 86400) * 10) / 10
    );
    const counts = sorted.map((e) => e.projectCount);
    return { meses, valores, counts };
  }, [entries]);

  const svg = useMemo(() => {
    if (!valores.length) return null;
    const w = 430;
    const h = 260;
    const padL = 34;
    const padR = 14;
    const padT = 24;
    const padB = 26;
    const maxVal = Math.max(...valores, 1);
    const maxY = Math.ceil((maxVal * 1.15) / 10) * 10 || 10;
    const stepY = maxY / 5;
    const stepX =
      valores.length > 1 ? (w - padL - padR) / (valores.length - 1) : 0;
    const yFor = (v: number) => padT + (1 - v / maxY) * (h - padT - padB);
    const xFor = (i: number) =>
      valores.length > 1 ? padL + i * stepX : (w - padL - padR) / 2 + padL;

    const gridLines = Array.from({ length: 6 }, (_, g) => {
      const v = Math.round(stepY * g);
      const y = yFor(v);
      return { v, y };
    });

    let pts = "";
    valores.forEach((v, i) => {
      pts += (i === 0 ? "M" : "L") + xFor(i) + " " + yFor(v) + " ";
    });

    return {
      w,
      h,
      padL,
      padR,
      gridLines,
      pts,
      points: valores.map((v, i) => ({
        x: xFor(i),
        y: yFor(v),
        v,
        mes: meses[i],
        count: counts[i],
      })),
    };
  }, [valores, meses, counts]);

  return (
    <Box sx={TE_CARD_SX}>
      <Typography sx={{ ...TE_PANEL_TITLE_SX, mb: 0.5 }}>
        Evolução do tempo médio total
      </Typography>
      <Typography sx={{ ...TE_PANEL_DESC_SX, mb: 1.5 }}>
        Média de dias corridos por mês de início do projeto (visão geral, não
        filtrada por célula/status).
      </Typography>

      {isLoading ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ py: 5 }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : !svg ? (
        <Typography
          sx={{
            color: TE_COLORS.textFaint,
            fontSize: 12.5,
            py: 2.5,
            textAlign: "center",
          }}
        >
          Sem projetos com data de início para montar a evolução mensal.
        </Typography>
      ) : (
        <>
          <svg viewBox={`0 0 ${svg.w} ${svg.h}`} width="100%" height={svg.h}>
            {svg.gridLines.map((g) => (
              <g key={g.v}>
                <line
                  x1={svg.padL}
                  y1={g.y}
                  x2={svg.w - svg.padR}
                  y2={g.y}
                  stroke="#EEF0F6"
                  strokeWidth={1}
                />
                <text
                  x={svg.padL - 8}
                  y={g.y + 3}
                  fontSize={9}
                  fill={TE_COLORS.textFaint}
                  textAnchor="end"
                >
                  {g.v}
                </text>
              </g>
            ))}
            <path
              d={svg.pts}
              fill="none"
              stroke={TE_COLORS.blue}
              strokeWidth={2.5}
            />
            {svg.points.map((p) => (
              <g key={p.mes}>
                <circle cx={p.x} cy={p.y} r={4} fill={TE_COLORS.blue}>
                  <title>
                    {p.mes}: {p.v} dias ({p.count} projeto(s))
                  </title>
                </circle>
                <text
                  x={p.x}
                  y={p.y - 10}
                  fontSize={10.5}
                  fontWeight={600}
                  fill={TE_COLORS.text}
                  textAnchor="middle"
                >
                  {p.v}
                </text>
                <text
                  x={p.x}
                  y={svg.h - 8}
                  fontSize={9.5}
                  fill={TE_COLORS.textFaint}
                  textAnchor="middle"
                >
                  {p.mes}
                </text>
              </g>
            ))}
          </svg>
          <Box
            display="flex"
            alignItems="center"
            gap={0.75}
            sx={{ fontSize: 11.5, color: TE_COLORS.textDim, mt: 0.5 }}
          >
            <Box
              sx={{
                width: 10,
                height: 3,
                bgcolor: TE_COLORS.blue,
                borderRadius: "2px",
              }}
            />
            Tempo médio total (dias) — agrupado pelo mês de início do projeto
          </Box>
        </>
      )}
    </Box>
  );
}
